import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { type TipoToast } from '@/shared/ui/Toast';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import { IconChevronLeft, IconTrash } from '@/shared/ui/Icon';
import {
  kanbanApi,
  type ContagemEtapas,
  type RemanejamentoEtapa,
} from '@/features/orcamentos/api/kanbanApi';
import {
  ACAO_DESCRICAO,
  ACAO_ROTULO,
  ACOES_ETAPA,
  ETAPA_CANCELADO,
  ETAPA_FECHADO,
  ETAPA_INICIAL,
  LIMITE_ETAPAS,
  LIMITE_ROTULO,
  destinosDeAprovacao,
  ehProtegida,
  ehTerminal,
  normalizarEtapas,
  novoIdEtapa,
  ordenarEtapas,
  type AcaoEtapa,
  type EtapaKanban,
} from '@/features/orcamentos/api/etapasKanban';
import { useEtapasKanban } from '@/shared/state/EtapasKanbanContext';
import { plataforma } from '@/platform';

/** Cores sugeridas — o usuário ainda pode escolher qualquer outra. */
const PALETA = [
  '#2563eb',
  '#0891b2',
  '#7c3aed',
  '#c026d3',
  '#d97706',
  '#ea580c',
  '#16a34a',
  '#0f766e',
  '#64748b',
  '#be123c',
];

const CONFIRMACAO_NOVA = {
  titulo: 'Confirmar mudança de etapa?',
  mensagem: 'Deseja mudar a ordem de serviço {numero} para {etapa}?',
  aviso: null as string | null,
};

function mensagemDeErro(e: unknown): string {
  return e instanceof Error ? e.message : 'Falha ao salvar as etapas.';
}

/**
 * Configuração das colunas do kanban de ordens de serviço.
 *
 * A oficina cria as etapas que fizerem sentido para o fluxo dela — dá
 * nome, escolhe a cor, define quais botões o card mostra e se a mudança
 * pede confirmação. Três etapas não saem da lista: a inicial (onde toda
 * OS nasce), "fechado pago" e "cancelado", que são os dois fins de linha.
 */
export function EtapasKanbanCard({
  onToast,
}: {
  onToast: (t: { mensagem: string; tipo: TipoToast }) => void;
}) {
  const { recarregar: recarregarGlobal } = useEtapasKanban();
  const dialogos = useDialogos();
  const [salvo, setSalvo] = useState<EtapaKanban[]>([]);
  const [rascunho, setRascunho] = useState<EtapaKanban[]>([]);
  const [contagem, setContagem] = useState<ContagemEtapas>({});
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [expandida, setExpandida] = useState<string | null>(null);
  // Etapas apagadas no rascunho que ainda têm OS: cada uma precisa dizer
  // para onde as ordens vão antes do salvamento passar.
  const [remanejar, setRemanejar] = useState<Record<string, string>>({});
  const [excluindo, setExcluindo] = useState<EtapaKanban | null>(null);
  // Alguém mexeu nas etapas em outra máquina enquanto havia edição aqui.
  const [mudouEmOutraMaquina, setMudouEmOutraMaquina] = useState(false);
  const [destinoExclusao, setDestinoExclusao] = useState<string>(ETAPA_INICIAL);

  async function carregar() {
    setCarregando(true);
    setErro(null);
    try {
      const r = await kanbanApi.obterEtapas();
      const etapas = normalizarEtapas(r.etapas);
      setSalvo(etapas);
      setRascunho(etapas);
      setContagem(r.contagem ?? {});
      setRemanejar({});
      setMudouEmOutraMaquina(false);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar as etapas.');
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    void carregar();
    // Uma carga por montagem — o resto é ação do usuário.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const alterou = useMemo(
    () => JSON.stringify(rascunho) !== JSON.stringify(salvo),
    [rascunho, salvo],
  );

  /*
   * As etapas são configuração da conta: quem mexe nelas pode estar no
   * navegador ou em outro desktop. O kanban já se atualizava sozinho (o
   * provider ouve o mesmo aviso), mas esta tela só lia na montagem — quem
   * estivesse com ela aberta continuava vendo a lista velha.
   *
   * Com edição em andamento não recarregamos por conta própria: sobrescrever
   * o que a pessoa está digitando é pior do que mostrar dado velho. Nesse
   * caso avisamos e deixamos a decisão de descartar com ela.
   */
  // As flags vivem em refs para o ouvinte não precisar ser refeito a cada
  // tecla digitada.
  const alterouRef = useRef(alterou);
  alterouRef.current = alterou;
  const salvandoRef = useRef(salvando);
  salvandoRef.current = salvando;

  useEffect(
    () =>
      plataforma.aoAtualizarDados(() => {
        if (alterouRef.current || salvandoRef.current) {
          setMudouEmOutraMaquina(true);
          return;
        }
        void carregar();
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  function atualizar(id: string, mudanca: Partial<EtapaKanban>) {
    setRascunho((atual) =>
      atual.map((e) => (e.id === id ? { ...e, ...mudanca } : e)),
    );
  }

  /**
   * Antes de gravar, as etapas que nunca foram salvas ganham um id
   * derivado do nome que a oficina escolheu ("Lavagem" → `Lavagem`). O id
   * não é derivado enquanto se digita porque trocá-lo remonta a linha e
   * o campo perderia o foco a cada tecla. Etapa já salva mantém o id: é
   * ele que amarra as O.S. à coluna.
   */
  function comIdsDefinitivos(lista: EtapaKanban[]): {
    etapas: EtapaKanban[];
    trocas: Map<string, string>;
  } {
    const persistidos = new Set(salvo.map((e) => e.id));
    const usados = new Set(
      lista.filter((e) => persistidos.has(e.id) || ehProtegida(e.id)).map((e) => e.id),
    );
    const trocas = new Map<string, string>();
    const comId = lista.map((e) => {
      if (persistidos.has(e.id) || ehProtegida(e.id)) return e;
      const id = novoIdEtapa(e.rotulo || e.id, [...usados]);
      usados.add(id);
      if (id !== e.id) trocas.set(e.id, id);
      return { ...e, id };
    });
    // Uma etapa nova pode ser o destino de aprovação de outra: se o id dela
    // mudou agora, o destino tem que seguir junto — senão a normalização
    // encontra um destino inexistente e desliga o botão.
    const etapas = comId.map((e) =>
      e.aprovacaoDestino && trocas.has(e.aprovacaoDestino)
        ? { ...e, aprovacaoDestino: trocas.get(e.aprovacaoDestino)! }
        : e,
    );
    return { etapas, trocas };
  }

  /**
   * Liga/desliga o botão de aprovação do link compartilhado. Ao ligar já
   * sugerimos um destino — a próxima coluna não-final do quadro, que é o
   * caminho natural do fluxo — para o usuário não precisar escolher nada
   * quando o padrão já serve.
   */
  function alternarAprovacao(etapa: EtapaKanban, ligar: boolean) {
    if (!ligar) {
      atualizar(etapa.id, { aprovacaoDestino: null });
      return;
    }
    const indice = rascunho.findIndex((e) => e.id === etapa.id);
    const seguinte = rascunho
      .slice(indice + 1)
      .find((e) => !ehTerminal(e.id) && e.id !== etapa.id);
    const sugerido = seguinte ?? destinosDeAprovacao(rascunho, etapa.id)[0];
    atualizar(etapa.id, { aprovacaoDestino: sugerido?.id ?? null });
  }

  function mover(id: string, direcao: -1 | 1) {
    setRascunho((atual) => {
      const i = atual.findIndex((e) => e.id === id);
      const j = i + direcao;
      if (i < 0 || j < 0 || j >= atual.length) return atual;
      // Etapa protegida não troca de lugar: a inicial fica na frente, as
      // finais no fim.
      if (ehProtegida(atual[i].id) || ehProtegida(atual[j].id)) return atual;
      const copia = [...atual];
      [copia[i], copia[j]] = [copia[j], copia[i]];
      return ordenarEtapas(copia);
    });
  }

  function adicionar() {
    if (rascunho.length >= LIMITE_ETAPAS) {
      onToast({
        mensagem: `O kanban aceita no máximo ${LIMITE_ETAPAS} etapas.`,
        tipo: 'erro',
      });
      return;
    }
    const rotulo = 'Nova etapa';
    const id = novoIdEtapa(
      rotulo,
      rascunho.map((e) => e.id),
    );
    const nova: EtapaKanban = {
      id,
      rotulo,
      cor: PALETA[rascunho.length % PALETA.length],
      acoes: ['editar'],
      confirmacao: null,
      alertaAtraso: true,
      aprovacaoDestino: null,
    };
    setRascunho((atual) => ordenarEtapas([...atual, nova]));
    setExpandida(id);
  }

  function pedirExclusao(etapa: EtapaKanban) {
    const qtd = contagem[etapa.id] ?? 0;
    if (qtd === 0) {
      setRascunho((atual) => atual.filter((e) => e.id !== etapa.id));
      setRemanejar(({ [etapa.id]: _, ...resto }) => resto);
      return;
    }
    // Tem OS parada nessa coluna: o usuário escolhe o destino.
    setDestinoExclusao(
      rascunho.find((e) => e.id !== etapa.id && !ehTerminal(e.id))?.id ?? ETAPA_INICIAL,
    );
    setExcluindo(etapa);
  }

  function confirmarExclusao() {
    if (!excluindo) return;
    const alvo = excluindo.id;
    setRascunho((atual) => atual.filter((e) => e.id !== alvo));
    setRemanejar((atual) => ({ ...atual, [alvo]: destinoExclusao }));
    setExcluindo(null);
  }

  async function salvar() {
    setSalvando(true);
    setErro(null);
    try {
      const { etapas: aSalvar, trocas } = comIdsDefinitivos(rascunho);
      const idsFinais = new Set(aSalvar.map((e) => e.id));
      const movimentos: RemanejamentoEtapa[] = Object.entries(remanejar)
        .map(([de, para]) => ({ de, para: trocas.get(para) ?? para }))
        .filter(({ de, para }) => !idsFinais.has(de) && idsFinais.has(para));
      const r = await kanbanApi.salvarEtapas({ etapas: aSalvar, remanejar: movimentos });
      const etapas = normalizarEtapas(r.etapas);
      setSalvo(etapas);
      setRascunho(etapas);
      setContagem(r.contagem ?? {});
      setRemanejar({});
      await recarregarGlobal();
      onToast({ mensagem: 'Etapas do kanban salvas.', tipo: 'sucesso' });
    } catch (e) {
      setErro(mensagemDeErro(e));
      onToast({ mensagem: mensagemDeErro(e), tipo: 'erro' });
    } finally {
      setSalvando(false);
    }
  }

  async function restaurar() {
    const ok = await dialogos.confirmar({
      titulo: 'Restaurar etapas padrão',
      mensagem:
        'Isso volta o kanban às etapas de fábrica. As ordens de serviço que estiverem em etapas criadas por você voltam para a primeira coluna. Deseja continuar?',
      textoConfirmar: 'Restaurar',
      perigoso: true,
    });
    if (!ok) return;
    setSalvando(true);
    try {
      const r = await kanbanApi.restaurarPadrao();
      const etapas = normalizarEtapas(r.etapas);
      setSalvo(etapas);
      setRascunho(etapas);
      setContagem(r.contagem ?? {});
      setRemanejar({});
      await recarregarGlobal();
      onToast({ mensagem: 'Etapas restauradas.', tipo: 'sucesso' });
    } catch (e) {
      onToast({ mensagem: mensagemDeErro(e), tipo: 'erro' });
    } finally {
      setSalvando(false);
    }
  }

  const destinosPossiveis = rascunho.filter(
    (e) => e.id !== excluindo?.id && !ehTerminal(e.id),
  );

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Etapas do kanban de ordens de serviço</span>
        <div className="row-actions">
          <Button onClick={() => void restaurar()} disabled={salvando || carregando}>
            Restaurar padrão
          </Button>
          <Button onClick={adicionar} disabled={salvando || carregando}>
            + Nova etapa
          </Button>
        </div>
      </div>
      <div className="card__body">
        <p className="field__hint" style={{ marginBottom: 'var(--space-3)' }}>
          Cada etapa é uma coluna do kanban. Você escolhe o nome, a cor, quais
          botões aparecem nos cards daquela coluna e se mudar para ela pede
          confirmação. <strong>{rotuloOu(salvo, ETAPA_FECHADO, 'Fechado pago')}</strong> e{' '}
          <strong>{rotuloOu(salvo, ETAPA_CANCELADO, 'Cancelado')}</strong> são etapas
          finais: cor fixa, sempre no fim do quadro e a ordem de serviço fica
          somente para consulta. A primeira coluna é onde toda O.S. nasce, então
          ela também não pode ser removida.
        </p>

        {erro && (
          <p role="alert" className="alert alert--error">
            {erro}
          </p>
        )}

        {mudouEmOutraMaquina && (
          <div role="status" className="alert alert--aviso">
            As etapas foram alteradas em outro dispositivo. Suas mudanças aqui
            ainda não foram salvas.{' '}
            <Button variante="ghost" onClick={() => void carregar()} disabled={salvando}>
              Descartar e recarregar
            </Button>
          </div>
        )}

        {carregando ? (
          <p className="field__hint">Carregando…</p>
        ) : (
          <ul className="etapas">
            {rascunho.map((etapa, indice) => {
              const fixa = ehTerminal(etapa.id);
              const protegida = ehProtegida(etapa.id);
              const qtd = contagem[etapa.id] ?? 0;
              const aberta = expandida === etapa.id;
              const podeSubir =
                !protegida && indice > 0 && !ehProtegida(rascunho[indice - 1].id);
              const podeDescer =
                !protegida &&
                indice < rascunho.length - 1 &&
                !ehProtegida(rascunho[indice + 1].id);
              const destinosAprovacao = destinosDeAprovacao(rascunho, etapa.id);
              return (
                <li key={etapa.id} className="etapa">
                  <div className="etapa__topo">
                    <span className="etapa__cor" style={{ background: etapa.cor }} />
                    <button
                      type="button"
                      className="etapa__nome"
                      onClick={() => setExpandida(aberta ? null : etapa.id)}
                      aria-expanded={aberta}
                    >
                      <strong>{etapa.rotulo || '(sem nome)'}</strong>
                      <span className="etapa__meta">
                        {qtd > 0 ? `${qtd} O.S.` : 'nenhuma O.S.'}
                        {fixa ? ' · etapa final' : ''}
                        {etapa.id === ETAPA_INICIAL ? ' · etapa inicial' : ''}
                      </span>
                    </button>
                    <div className="etapa__acoes">
                      <Button
                        className="btn--icon etapa__mover"
                        aria-label={`Mover ${etapa.rotulo} para a esquerda`}
                        title="Mover para a esquerda"
                        disabled={!podeSubir}
                        onClick={() => mover(etapa.id, -1)}
                      >
                        <IconChevronLeft />
                      </Button>
                      <Button
                        className="btn--icon etapa__mover etapa__mover--direita"
                        aria-label={`Mover ${etapa.rotulo} para a direita`}
                        title="Mover para a direita"
                        disabled={!podeDescer}
                        onClick={() => mover(etapa.id, 1)}
                      >
                        <IconChevronLeft />
                      </Button>
                      <Button
                        className="btn--icon btn--danger"
                        aria-label={`Excluir etapa ${etapa.rotulo}`}
                        title={
                          protegida
                            ? 'Esta etapa não pode ser removida.'
                            : 'Excluir etapa'
                        }
                        disabled={protegida}
                        onClick={() => pedirExclusao(etapa)}
                      >
                        <IconTrash />
                      </Button>
                    </div>
                  </div>

                  {aberta && (
                    <div className="etapa__corpo">
                      <div className="etapa__linha">
                        <label className="field">
                          <span className="field__label">Nome da coluna</span>
                          <input
                            className="input"
                            value={etapa.rotulo}
                            maxLength={LIMITE_ROTULO}
                            onChange={(ev) =>
                              atualizar(etapa.id, { rotulo: ev.target.value })
                            }
                          />
                        </label>
                        <label className="field etapa__campo-cor">
                          <span className="field__label">Cor</span>
                          <input
                            className="etapa__color"
                            type="color"
                            value={etapa.cor}
                            disabled={fixa}
                            onChange={(ev) => atualizar(etapa.id, { cor: ev.target.value })}
                            aria-label={`Cor da etapa ${etapa.rotulo}`}
                          />
                          {fixa ? (
                            <span className="field__hint">
                              Cor fixa: {etapa.id === ETAPA_FECHADO ? 'verde' : 'vermelho'}.
                            </span>
                          ) : (
                            <span className="etapa__paleta">
                              {PALETA.map((c) => (
                                <button
                                  key={c}
                                  type="button"
                                  className={`etapa__swatch${etapa.cor === c ? ' etapa__swatch--ativo' : ''}`}
                                  style={{ background: c }}
                                  aria-label={`Usar a cor ${c}`}
                                  onClick={() => atualizar(etapa.id, { cor: c })}
                                />
                              ))}
                            </span>
                          )}
                        </label>
                      </div>

                      <fieldset className="etapa__bloco">
                        <legend className="field__label">
                          Botões que aparecem nos cards desta coluna
                        </legend>
                        {fixa ? (
                          <p className="field__hint">
                            Etapa final: o card mostra apenas o botão de visualizar.
                          </p>
                        ) : (
                          <>
                            <p className="field__hint">
                              O botão de visualizar aparece sempre.
                            </p>
                            <div className="etapa__acoes-lista">
                              {ACOES_ETAPA.map((acao) => (
                                <label key={acao} className="checkbox">
                                  <input
                                    type="checkbox"
                                    checked={etapa.acoes.includes(acao)}
                                    onChange={(ev) =>
                                      atualizar(etapa.id, {
                                        acoes: alternarAcao(
                                          etapa.acoes,
                                          acao,
                                          ev.target.checked,
                                        ),
                                      })
                                    }
                                  />
                                  <span>
                                    {ACAO_ROTULO[acao]}
                                    <span className="field__hint etapa__acao-dica">
                                      {ACAO_DESCRICAO[acao]}
                                    </span>
                                  </span>
                                </label>
                              ))}
                            </div>
                          </>
                        )}
                      </fieldset>

                      {!fixa && (
                        <label className="checkbox">
                          <input
                            type="checkbox"
                            checked={etapa.alertaAtraso}
                            onChange={(ev) =>
                              atualizar(etapa.id, { alertaAtraso: ev.target.checked })
                            }
                          />
                          <span>
                            Avisar quando a data de entrega passar
                            <span className="field__hint etapa__acao-dica">
                              Desligue em etapas onde o serviço já saiu da oficina.
                            </span>
                          </span>
                        </label>
                      )}

                      {!fixa && (
                        <fieldset className="etapa__bloco">
                          <legend className="field__label">
                            Botão de aprovação (link compartilhado)
                          </legend>
                          {destinosAprovacao.length === 0 ? (
                            <p className="field__hint">
                              Crie outra coluna que não seja final para poder usar a
                              aprovação — o cliente precisa ter para onde mandar a
                              ordem de serviço.
                            </p>
                          ) : (
                            <>
                              <label className="checkbox">
                                <input
                                  type="checkbox"
                                  checked={Boolean(etapa.aprovacaoDestino)}
                                  onChange={(ev) =>
                                    alternarAprovacao(etapa, ev.target.checked)
                                  }
                                />
                                <span>
                                  Mostrar botão "Aprovar" no link compartilhado
                                  <span className="field__hint etapa__acao-dica">
                                    Quem abrir o link de acompanhamento de uma O.S.
                                    parada nesta coluna vê um botão na cor desta
                                    etapa. Ao tocar nele, a ordem muda de coluna
                                    sozinha e a oficina vê a mudança na hora.
                                  </span>
                                </span>
                              </label>
                              {etapa.aprovacaoDestino && (
                                <div className="etapa__aprovacao">
                                  <label className="field">
                                    <span className="field__label">
                                      Ao aprovar, mover a O.S. para
                                    </span>
                                    <select
                                      className="input"
                                      value={etapa.aprovacaoDestino}
                                      onChange={(ev) =>
                                        atualizar(etapa.id, {
                                          aprovacaoDestino: ev.target.value,
                                        })
                                      }
                                    >
                                      {destinosAprovacao.map((d) => (
                                        <option key={d.id} value={d.id}>
                                          {d.rotulo || d.id}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <span className="field__hint">
                                    <strong>
                                      {rotuloOu(rascunho, ETAPA_FECHADO, 'Fechado pago')}
                                    </strong>{' '}
                                    e{' '}
                                    <strong>
                                      {rotuloOu(rascunho, ETAPA_CANCELADO, 'Cancelado')}
                                    </strong>{' '}
                                    ficam fora da lista: o cliente não fecha nem
                                    cancela a ordem pelo link.
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                        </fieldset>
                      )}

                      <fieldset className="etapa__bloco">
                        <legend className="field__label">
                          Pergunta ao mover uma O.S. para esta coluna
                        </legend>
                        <label className="checkbox">
                          <input
                            type="checkbox"
                            checked={Boolean(etapa.confirmacao)}
                            disabled={fixa}
                            onChange={(ev) =>
                              atualizar(etapa.id, {
                                confirmacao: ev.target.checked
                                  ? { ...CONFIRMACAO_NOVA }
                                  : null,
                              })
                            }
                          />
                          <span>
                            Pedir confirmação
                            {fixa && (
                              <span className="field__hint etapa__acao-dica">
                                Obrigatória nas etapas finais — o texto você escolhe.
                              </span>
                            )}
                          </span>
                        </label>
                        {etapa.confirmacao && (
                          <div className="etapa__confirmacao">
                            <label className="field">
                              <span className="field__label">Título</span>
                              <input
                                className="input"
                                value={etapa.confirmacao.titulo}
                                onChange={(ev) =>
                                  atualizar(etapa.id, {
                                    confirmacao: {
                                      ...etapa.confirmacao!,
                                      titulo: ev.target.value,
                                    },
                                  })
                                }
                              />
                            </label>
                            <label className="field">
                              <span className="field__label">Pergunta</span>
                              <textarea
                                className="input"
                                rows={2}
                                value={etapa.confirmacao.mensagem}
                                onChange={(ev) =>
                                  atualizar(etapa.id, {
                                    confirmacao: {
                                      ...etapa.confirmacao!,
                                      mensagem: ev.target.value,
                                    },
                                  })
                                }
                              />
                              <span className="field__hint">
                                Use <code>{'{numero}'}</code> para o número da O.S. e{' '}
                                <code>{'{etapa}'}</code> para o nome da coluna.
                              </span>
                            </label>
                            <label className="field">
                              <span className="field__label">Aviso em destaque (opcional)</span>
                              <textarea
                                className="input"
                                rows={2}
                                value={etapa.confirmacao.aviso ?? ''}
                                onChange={(ev) =>
                                  atualizar(etapa.id, {
                                    confirmacao: {
                                      ...etapa.confirmacao!,
                                      aviso: ev.target.value.trim() === '' ? null : ev.target.value,
                                    },
                                  })
                                }
                              />
                            </label>
                          </div>
                        )}
                      </fieldset>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {Object.keys(remanejar).length > 0 && (
          <p className="alert alert--aviso" role="status">
            Ao salvar, as ordens de serviço das etapas removidas serão movidas para
            a coluna escolhida.
          </p>
        )}

        <div className="row-actions" style={{ marginTop: 12 }}>
          <Button onClick={() => void carregar()} disabled={!alterou || salvando}>
            Descartar
          </Button>
          <Button
            variante="primary"
            onClick={() => void salvar()}
            disabled={!alterou || salvando}
          >
            {salvando ? 'Salvando…' : 'Salvar etapas'}
          </Button>
        </div>
      </div>

      <Modal
        aberto={Boolean(excluindo)}
        titulo={`Excluir a etapa "${excluindo?.rotulo ?? ''}"?`}
        onFechar={() => setExcluindo(null)}
        rodape={
          <>
            <Button onClick={() => setExcluindo(null)}>Cancelar</Button>
            <Button
              variante="primary"
              onClick={confirmarExclusao}
              disabled={destinosPossiveis.length === 0}
            >
              Remover etapa
            </Button>
          </>
        }
      >
        <div className="form-grid">
          <p>
            Há <strong>{excluindo ? (contagem[excluindo.id] ?? 0) : 0}</strong> ordem(ns)
            de serviço nesta coluna. Escolha para onde elas devem ir.
          </p>
          <label className="field">
            <span className="field__label">Mover as ordens de serviço para</span>
            <select
              className="input"
              value={destinoExclusao}
              onChange={(e) => setDestinoExclusao(e.target.value)}
            >
              {destinosPossiveis.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.rotulo}
                </option>
              ))}
            </select>
          </label>
          <p className="field__hint">
            A mudança só acontece quando você clicar em "Salvar etapas".
          </p>
        </div>
      </Modal>
    </div>
  );
}

function alternarAcao(atuais: AcaoEtapa[], acao: AcaoEtapa, ligar: boolean): AcaoEtapa[] {
  if (ligar) return atuais.includes(acao) ? atuais : [...atuais, acao];
  return atuais.filter((a) => a !== acao);
}

function rotuloOu(etapas: EtapaKanban[], id: string, padrao: string): string {
  return etapas.find((e) => e.id === id)?.rotulo ?? padrao;
}
